import type { FC, ReactNode } from 'react'

import { Plane } from 'lucide-react'

// A deterministic barcode, the same on the server and in the browser
const BARS = Array.from('MACHGOPRIVATEAVIATIONANYWHERE').flatMap((character) => {
    const code = character.charCodeAt(0)
    return [(code % 3) + 1, ((code >> 2) % 2) + 1]
})

const BAR_SHAPES = BARS.reduce<{ x: number; width: number; filled: boolean }[]>((shapes, width, index) => {
    const previous = shapes[shapes.length - 1]
    const x = previous ? previous.x + previous.width : 0
    shapes.push({ x, width, filled: index % 2 === 0 })
    return shapes
}, [])

const BAR_TOTAL = BAR_SHAPES.reduce((total, shape) => total + shape.width, 0)

const BoardingPass: FC = (): ReactNode => {
    return (
        <div className='relative overflow-hidden rounded-[18px] bg-ivory text-obsidian shadow-[0_40px_90px_rgba(0,0,0,0.55)]'>
            <div className='px-6 pb-5 pt-6'>
                <div className='flex items-start justify-between'>
                    <span className='pl-[0.3em] font-heading text-[1.35rem] font-medium leading-none tracking-[0.3em]'>MACHGO</span>
                    <span className='text-[9px] uppercase tracking-[0.24em] text-ash'>Boarding pass</span>
                </div>

                <div className='mt-7 flex items-end justify-between gap-3'>
                    <div>
                        <p className='text-[9px] uppercase tracking-[0.24em] text-ash'>From</p>
                        <p className='mt-1 font-heading text-[1.55rem] leading-[1.05]'>Where you are</p>
                    </div>
                    <Plane className='mb-1.5 size-4 shrink-0 text-gilt-deep' />
                    <div className='text-right'>
                        <p className='text-[9px] uppercase tracking-[0.24em] text-ash'>To</p>
                        <p className='mt-1 font-heading text-[1.55rem] leading-[1.05]'>Where you wish</p>
                    </div>
                </div>

                <div className='mt-6 grid grid-cols-3 gap-3 border-t border-obsidian/15 pt-4'>
                    {[
                        { label: 'Passenger', value: 'You' },
                        { label: 'Departure', value: 'Your time' },
                        { label: 'Seat', value: 'Any' }
                    ].map((item) => (
                        <div key={item.label}>
                            <p className='text-[9px] uppercase tracking-[0.22em] text-ash'>{item.label}</p>
                            <p className='mt-1 text-[13px] font-medium'>{item.value}</p>
                        </div>
                    ))}
                </div>
            </div>

            <div className='relative flex items-center'>
                <span className='absolute -left-3 size-6 rounded-full bg-obsidian' />
                <span className='mx-5 h-px flex-1 border-t border-dashed border-obsidian/25' />
                <span className='absolute -right-3 size-6 rounded-full bg-obsidian' />
            </div>

            <div className='flex items-center justify-between gap-5 px-6 pb-6 pt-5'>
                <svg viewBox={`0 0 ${BAR_TOTAL} 20`} preserveAspectRatio='none' aria-hidden='true' className='h-10 w-full max-w-[200px]'>
                    {BAR_SHAPES.filter((shape) => shape.filled).map((shape) => (
                        <rect key={shape.x} x={shape.x} y={0} width={shape.width} height={20} fill='currentColor' />
                    ))}
                </svg>
                <span className='shrink-0 text-right text-[9px] uppercase leading-relaxed tracking-[0.22em] text-ash'>
                    Private
                    <br />
                    aviation
                </span>
            </div>
        </div>
    )
}

export default BoardingPass
